import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Select, SelectItem, Spinner } from '@heroui/react';
import { GraduationCap, ArrowRight } from 'lucide-react';
import { useAuth } from '@src/presentation/contexts/AuthContext';
import { useUniversities } from '@src/presentation/hooks/universities';
import { ToastService } from '@src/shared/services/toast.service';
import { getOnboardingRoute } from '@src/application/users/dto/auth-response.dto';

export default function OnboardingUniversityPage() {
    const navigate = useNavigate();
    const { setUniversity } = useAuth();
    const { data: universitiesData, isLoading: isLoadingUniversities } = useUniversities(0, 100);

    const [selectedUniversity, setSelectedUniversity] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Route protection is handled by OnboardingRoute guard

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedUniversity) {
            ToastService.error('Please select your university');
            return;
        }

        setIsSubmitting(true);

        try {
            const newProfileState = await setUniversity(selectedUniversity);

            // Navigate to the appropriate route based on the new profile state
            navigate(getOnboardingRoute(newProfileState), { replace: true });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to set university';
            ToastService.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const universities = universitiesData?.universities || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50/30">
            <main className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-center min-h-screen py-12">
                    <div className="w-full max-w-md">
                        {/* Logo & Title Section */}
                        <div className="text-center mb-8 space-y-3">
                            <div className="flex justify-center mb-4">
                                <div className="flex h-14 w-14 items-center justify-center rounded bg-primary/10 ring-1 ring-primary/20">
                                    <GraduationCap className="h-7 w-7 text-primary" />
                                </div>
                            </div>
                            <h1 className="text-3xl font-bold tracking-tight text-foreground">Select Your University</h1>
                            <p className="text-default-500">
                                Choose your university to personalize your PascoHub experience
                            </p>
                        </div>

                        {/* Progress indicator */}
                        <div className="flex justify-center mb-6">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-primary"></div>
                                <div className="w-8 h-1 bg-default-200 rounded"></div>
                                <div className="w-3 h-3 rounded-full bg-default-200"></div>
                            </div>
                            <span className="ml-3 text-sm text-default-500">Step 1 of 2</span>
                        </div>

                        {/* Form Card */}
                        <Card className="border border-border bg-card backdrop-blur-sm rounded shadow-none hover:shadow-custom transition-shadow duration-200">
                            <form onSubmit={handleSubmit}>
                                <CardBody className="gap-5 p-6 sm:p-8">
                                    {isLoadingUniversities ? (
                                        <div className="flex justify-center py-8">
                                            <Spinner size="lg" />
                                        </div>
                                    ) : (
                                        <Select
                                            label="University"
                                            placeholder="Select your university"
                                            selectedKeys={selectedUniversity ? [selectedUniversity] : []}
                                            onSelectionChange={(keys) => {
                                                const selected = Array.from(keys)[0] as string;
                                                setSelectedUniversity(selected || '');
                                            }}
                                            isDisabled={isSubmitting}
                                            isRequired
                                            variant="bordered"
                                            size="lg"
                                            classNames={{
                                                base: "transition-all duration-200",
                                                label: "text-default-600 font-medium",
                                                trigger: "rounded border-default-200 data-[hover=true]:border-default-400 data-[open=true]:border-primary data-[focus=true]:border-primary",
                                            }}
                                        >
                                            {universities.map((university) => (
                                                <SelectItem key={university.university_id} textValue={university.name}>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{university.name}</span>
                                                        {university.address && (
                                                            <span className="text-xs text-default-400">{university.address}</span>
                                                        )}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    )}

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        color="primary"
                                        size="lg"
                                        className="w-full mt-2 rounded font-semibold"
                                        isLoading={isSubmitting}
                                        isDisabled={!selectedUniversity || isSubmitting}
                                        endContent={!isSubmitting && <ArrowRight className="w-4 h-4" />}
                                    >
                                        {isSubmitting ? 'Setting up...' : 'Continue'}
                                    </Button>
                                </CardBody>
                            </form>
                        </Card>

                        {/* Help text */}
                        <p className="text-center text-xs text-default-400 mt-6">
                            Can't find your university? Contact us at support@pascohub.com
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
}
